"use client";

import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { usePackProducts } from "@/features/products/hook/usePackProducts";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@/components/ui/carousel";
import { Skeleton } from "@/components/ui/skeleton";
import Autoplay from "embla-carousel-autoplay";

export const FeaturedPack = () => {
    const { data: response, isLoading } = usePackProducts({ limit: 5 });
    const packs = response?.data?.data || [];

    if (isLoading) {
        return (
            <section className="bg-primary-50 py-10 md:py-16 overflow-hidden">
                <div className="container mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
                    <Skeleton className="w-full h-75 sm:h-100 lg:h-125 rounded-3xl" />
                    <div className="space-y-8">
                        <div className="space-y-4">
                            <Skeleton className="h-10 w-3/4" />
                            <Skeleton className="h-6 w-full" />
                            <Skeleton className="h-6 w-5/6" />
                        </div>
                        <Skeleton className="h-12 w-40" />
                    </div>
                </div>
            </section>
        );
    }

    if (!packs || packs.length === 0) {
        return null; // Or show a fallback if needed
    }

    return (
        <section className="bg-primary-50 py-10 md:py-16 overflow-hidden">
            <div className="container mx-auto px-6">
                <Carousel
                    opts={{
                        align: "start",
                        loop: true,
                    }}
                    plugins={[
                        Autoplay({
                            delay: 5000,
                            stopOnInteraction: true,
                        }),
                    ]}
                    className="w-full"
                >
                    <CarouselContent>
                        {packs.map((pack) => (
                            <CarouselItem key={pack.id}>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
                                    {/* Left Side: Photography */}
                                    <div className="relative group">
                                        <div className="absolute -inset-2 md:-inset-4 bg-primary-200/20 rounded-[40px] blur-2xl transition-all group-hover:bg-primary-200/30" />
                                        <div className="relative rounded-3xl overflow-hidden ">
                                            <Image
                                                src={(() => {
                                                    if (pack.images && pack.images.length > 0) {
                                                        const primary = pack.images.find((img: any) => img.isPrimary);
                                                        return primary ? primary.url : pack.images[0].url;
                                                    }
                                                    return pack.image || "/assets/landing/spice-pack.jpg";
                                                })()}
                                                alt={pack.name}
                                                width={700}
                                                height={700}
                                                className="w-full h-75 sm:h-100 lg:h-125 object-cover rounded-3xl"
                                                sizes="(min-width: 1024px) 50vw, 100vw"
                                                loading="lazy"
                                            />
                                        </div>
                                    </div>

                                    {/* Right Side: Content */}
                                    <div className="space-y-8 text-center lg:text-left">
                                        <div className="space-y-4">
                                            <h2 className="font-brastika text-2xl sm:text-3xl md:text-5xl text-primary-900">
                                                {pack.name}
                                            </h2>
                                            <div
                                                className="text-sm sm:text-base md:text-lg text-primary-700 max-w-lg mx-auto lg:mx-0 line-clamp-3 rich-text-content"
                                                dangerouslySetInnerHTML={{ __html: pack.descriptionHtml || pack.description || "Un pack soigneusement sélectionné pour vous." }}
                                            />
                                        </div>

                                        <Button
                                            size="lg"
                                            variant="default"
                                            className="w-full lg:w-auto h-12 cursor-pointer shadow-lg hover:shadow-primary-200/50 transition-all font-semibold"
                                            asChild
                                        >
                                            <Link href={`/products/${pack.slug}`}>
                                                Acheter maintenant
                                            </Link>
                                        </Button>
                                    </div>
                                </div>
                            </CarouselItem>
                        ))}
                    </CarouselContent>

                    {packs.length > 1 && (
                        <>
                            <CarouselPrevious className="hidden md:flex -left-12 lg:-left-16" />
                            <CarouselNext className="hidden md:flex -right-12 lg:-right-16" />
                        </>
                    )}
                </Carousel>
            </div>
        </section>
    );
};
